import type { SeoBrief, SeoContentType, SeoTargetKind } from '../seo-brief.schema';

/**
 * Score SEO de contenu « maison » — types publics.
 * Spec : `docs/to_implement/seo/seo-content-score-spec.md`.
 *
 * Tout est pur (pas de réseau, pas de DOM, pas de LLM) pour tourner à
 * l'identique dans le backend (gate QA), le front pro (badge en direct),
 * l'admin Payload et les scripts d'éval.
 */

export enum SeoScoreGroup {
  KEYWORD = 'KEYWORD',
  STRUCTURE = 'STRUCTURE',
  LENGTH_META = 'LENGTH_META',
  READABILITY = 'READABILITY',
  LINKS = 'LINKS',
  IMAGES = 'IMAGES',
  STYLE = 'STYLE',
  BRIEF = 'BRIEF',
}

export enum SeoRuleKey {
  // A. Ciblage du mot-clé
  KW_IN_TITLE = 'KW_IN_TITLE',
  KW_IN_META_TITLE = 'KW_IN_META_TITLE',
  KW_IN_META_DESC = 'KW_IN_META_DESC',
  KW_IN_SLUG = 'KW_IN_SLUG',
  KW_IN_INTRO = 'KW_IN_INTRO',
  KW_IN_HEADINGS = 'KW_IN_HEADINGS',
  KW_DENSITY = 'KW_DENSITY',
  KW_IN_IMAGE_ALT = 'KW_IN_IMAGE_ALT',
  KW_STUFFING = 'KW_STUFFING',
  SECONDARY_KW_COVERAGE = 'SECONDARY_KW_COVERAGE',
  // B. Structure
  SINGLE_H1 = 'SINGLE_H1',
  HEADING_HIERARCHY = 'HEADING_HIERARCHY',
  H2_RATIO = 'H2_RATIO',
  HEADING_LENGTH = 'HEADING_LENGTH',
  EMPTY_SECTION = 'EMPTY_SECTION',
  INTRO_PRESENT = 'INTRO_PRESENT',
  PARAGRAPH_LENGTH = 'PARAGRAPH_LENGTH',
  PARAGRAPH_VARIETY = 'PARAGRAPH_VARIETY',
  /** Paragraphes tous de la même taille et sections toutes de la même forme (2026.09.4). */
  PARAGRAPH_UNIFORMITY = 'PARAGRAPH_UNIFORMITY',
  LIST_OR_TABLE_SANITY = 'LIST_OR_TABLE_SANITY',
  FAQ_PRESENT = 'FAQ_PRESENT',
  // C. Longueur et métadonnées
  WORD_COUNT = 'WORD_COUNT',
  META_TITLE_LENGTH = 'META_TITLE_LENGTH',
  META_DESC_LENGTH = 'META_DESC_LENGTH',
  META_TITLE_UNIQUE = 'META_TITLE_UNIQUE',
  SLUG_QUALITY = 'SLUG_QUALITY',
  EXCERPT_PRESENT = 'EXCERPT_PRESENT',
  // D. Lisibilité (français)
  SENTENCE_LENGTH_AVG = 'SENTENCE_LENGTH_AVG',
  LONG_SENTENCES = 'LONG_SENTENCES',
  READABILITY_FR = 'READABILITY_FR',
  PASSIVE_VOICE = 'PASSIVE_VOICE',
  TRANSITION_WORDS = 'TRANSITION_WORDS',
  SAME_SENTENCE_START = 'SAME_SENTENCE_START',
  NOMINAL_STACKS = 'NOMINAL_STACKS',
  // E. Liens
  INTERNAL_LINKS_COUNT = 'INTERNAL_LINKS_COUNT',
  LINK_DISTRIBUTION = 'LINK_DISTRIBUTION',
  ANCHOR_QUALITY = 'ANCHOR_QUALITY',
  DUPLICATE_TARGET = 'DUPLICATE_TARGET',
  LINK_DENSITY = 'LINK_DENSITY',
  CONVERSION_LINK = 'CONVERSION_LINK',
  EXTERNAL_SOURCE = 'EXTERNAL_SOURCE',
  FORBIDDEN_LINK = 'FORBIDDEN_LINK',
  // F. Images
  IMAGE_PRESENT = 'IMAGE_PRESENT',
  IMAGE_ALT = 'IMAGE_ALT',
  IMAGE_FILENAME = 'IMAGE_FILENAME',
  IMAGE_WEIGHT = 'IMAGE_WEIGHT',
  // G. Style / anti-IA
  EM_DASH = 'EM_DASH',
  AI_PHRASES = 'AI_PHRASES',
  ANGLICISMS = 'ANGLICISMS',
  EXCLAMATIONS = 'EXCLAMATIONS',
  RHETORICAL_QUESTIONS = 'RHETORICAL_QUESTIONS',
  NGRAM_REPETITION = 'NGRAM_REPETITION',
  LEXICAL_DIVERSITY = 'LEXICAL_DIVERSITY',
  TRIPLET_OVERUSE = 'TRIPLET_OVERUSE',
  FORBIDDEN_TERMS = 'FORBIDDEN_TERMS',
  CONTACT_IN_BODY = 'CONTACT_IN_BODY',
  CTA_COUNT = 'CTA_COUNT',
  /** Raisonnement du modèle déversé dans le texte (« l'utilisateur me demande… »). */
  REASONING_LEAK = 'REASONING_LEAK',
  /** Mentions de source ou de consigne qui fuient dans le texte (« d'après notre grille tarifaire »). */
  SOURCE_LEAK = 'SOURCE_LEAK',
  /** Transitions mécaniques : « Le premier… Le second… », « D'abord… Ensuite… Enfin » (2026.09.4). */
  ENUMERATIVE_SCAFFOLDING = 'ENUMERATIVE_SCAFFOLDING',
  /** Introduction qui annonce l'article au lieu d'y entrer (« nous détaillons ici… ») (2026.09.4). */
  META_INTRO = 'META_INTRO',
  // H. Respect du brief
  OUTLINE_COVERAGE = 'OUTLINE_COVERAGE',
  PAA_COVERAGE = 'PAA_COVERAGE',
  ENTITY_COVERAGE = 'ENTITY_COVERAGE',
  FACT_TRACE = 'FACT_TRACE',
  LENGTH_VS_SERP = 'LENGTH_VS_SERP',
  /** Chaque lien interne prévu par le brief est présent sous forme de lien (pas en texte nu). */
  REQUIRED_LINKS = 'REQUIRED_LINKS',
  /** L'appel à l'action du brief est un lien vers son URL. */
  CTA_LINK = 'CTA_LINK',
}

export enum SeoRuleStatus {
  PASS = 'PASS',
  WARN = 'WARN',
  FAIL = 'FAIL',
  /** Non évaluable (pas de mot-clé, pas de brief, donnée absente) : hors barème. */
  SKIPPED = 'SKIPPED',
}

export enum SeoScoreGrade {
  EXCELLENT = 'EXCELLENT',
  GOOD = 'GOOD',
  NEEDS_WORK = 'NEEDS_WORK',
  POOR = 'POOR',
}

export interface SeoScoreImageInput {
  src: string;
  alt?: string;
  width?: number;
  bytes?: number;
}

export interface SeoScoreFaqItem {
  question: string;
  answer: string;
}

export interface SeoScoreInput {
  /** Corps de l'article en HTML (sans le H1 si `title` est fourni à part). */
  html: string;
  /**
   * Questions / réponses éditées à part du corps (champ `faq` d'une publication ou d'un article).
   * Notées comme une section « Questions fréquentes » placée en fin d'article : même barème que
   * lorsque la FAQ était rédigée dans le corps (bonus `FAQ_PRESENT`, couverture des questions,
   * mots comptés), et mêmes exclusions de style.
   */
  faq?: SeoScoreFaqItem[];
  /** Titre de l'article (H1). Si absent, le premier H1 du HTML est utilisé. */
  title?: string;
  meta_title?: string;
  meta_description?: string;
  slug?: string;
  excerpt?: string;
  /** Images connues hors HTML (hero) ou enrichies (poids, largeur). Fusionnées avec celles du HTML. */
  images?: SeoScoreImageInput[];
}

export interface SeoScoreOptions {
  locale?: 'fr-FR';
  /** Sans lui, les familles KEYWORD et BRIEF (partiellement) sont SKIPPED. */
  focus_keyword?: string;
  secondary_keywords?: string[];
  brief?: SeoBrief;
  target_word_count?: number;
  content_type?: SeoContentType;
  /** Hôtes considérés comme internes (ex. `monsite.fr`, `propelsia.ai`). Chemins relatifs = internes. */
  internal_hosts?: string[];
  /** Chemins (ou fragments d'URL) des pages de conversion attendues. */
  conversion_paths?: string[];
  /** Termes interdits (concurrents, allégations, médical…), comparés sans casse ni accents. */
  forbidden_terms?: string[];
  /** Domaines vers lesquels un lien est interdit. */
  forbidden_hosts?: string[];
  /** Cible : ajuste quelques seuils (nombre de liens internes, lien externe source). */
  target_kind?: SeoTargetKind;
  /** Mode strict (backend) : les règles bloquantes en échec sont remontées dans `blocking`. */
  strict?: boolean;
  /**
   * Les images ne sont pas encore choisies (QA du moteur avant `pickImages`) : la famille
   * Images est SKIPPED et renormalisée au lieu d'échouer sur un artefact incomplet. Le score
   * est recalculé sans ce flag une fois les images posées (`rescoreWithImages`).
   */
  images_pending?: boolean;
}

export interface SeoRuleResult {
  key: SeoRuleKey;
  status: SeoRuleStatus;
  /** Valeur mesurée, pour l'affichage (« 1,4 % », « 3 », « 48 »). */
  value?: string | number;
  /** Seuil ou plage attendue, pour l'affichage. */
  threshold?: string;
  /** true si la règle est bloquante et en FAIL (mode strict uniquement). */
  blocking: boolean;
  /** Extraits ou repères (titres, phrases) pour localiser le problème. */
  locations?: string[];
}

export interface SeoGroupResult {
  key: SeoScoreGroup;
  /** Poids nominal du groupe (somme des groupes = 100). */
  weight: number;
  /** Score du groupe sur 100 (règles SKIPPED exclues). */
  score: number;
  /** false si aucune règle du groupe n'a pu être évaluée. */
  evaluable: boolean;
  rules: SeoRuleResult[];
}

export interface SeoScoreStats {
  words: number;
  sentences: number;
  paragraphs: number;
  h1: number;
  h2: number;
  h3: number;
  images: number;
  internal_links: number;
  external_links: number;
  reading_time_min: number;
  keyword_occurrences: number;
  keyword_density: number;
  avg_sentence_length: number;
  readability: number;
  passive_ratio: number;
  transition_ratio: number;
}

export interface SeoScoreResult {
  /** Version du barème (seuils + poids) : deux scores ne se comparent qu'à version égale. */
  version: string;
  score: number;
  grade: SeoScoreGrade;
  /** Somme des poids des groupes réellement évalués (100 = tout évalué). */
  evaluable_weight: number;
  blocking: SeoRuleKey[];
  groups: SeoGroupResult[];
  stats: SeoScoreStats;
}
